class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // The EVORÍ header uses its own cart icon, while Dawn uses
    // #cart-icon-bubble. Support both without changing either header's markup.
    const cartLink = document.querySelector('[data-evori-cart-trigger]') || document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Monster is store-wide. Existing product forms still call this native
    // drawer API, so hand off here as well as on the custom branded PDP.
    // Never activate the full-screen native bag underneath the app drawer.
    if (this.usesMonsterCart()) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      if (this.classList.contains('active')) this.close();
      this.classList.remove('animate', 'active');
      return this.openMonsterCart(triggeredBy);
    }
    return this.openNativeCart(triggeredBy);
  }

  usesMonsterCart() {
    return !window.upsellCartStopped && (
      typeof window.monster_refresh === 'function' ||
      document.querySelector('script[src*="monster-upsells"][src*="cart_renderer"]') !== null
    );
  }

  async openMonsterCart(triggeredBy) {
    const deadline = Date.now() + 5000;
    while (this.usesMonsterCart() && Date.now() < deadline && (
      typeof window.monster_refresh !== 'function' || typeof window.openCart !== 'function'
    )) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (this.usesMonsterCart() && typeof window.monster_refresh === 'function' && typeof window.openCart === 'function') {
      try {
        await window.monster_refresh(true);
        return;
      } catch (error) {
        // An app outage must not strand an item already added to Shopify.
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
    }
    // Keep the native drawer usable if the merchant disables Monster later.
    // Once Monster starts loading, don't open a second drawer on a slow load.
    if (this.usesMonsterCart()) window.location.assign(window.routes?.cart_url || '/cart');
    else this.openNativeCart(triggeredBy);
  }

  openNativeCart(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.updateEvoriCartBadge(parsedState);

    if (this.usesMonsterCart()) {
      this.classList.remove('is-loading');
      return this.open();
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  updateEvoriCartBadge(parsedState) {
    let itemCount = Number(parsedState?.item_count);

    if (!Number.isFinite(itemCount)) {
      const cartIconSection = parsedState?.sections?.['cart-icon-bubble'];
      if (cartIconSection) {
        const iconDocument = new DOMParser().parseFromString(cartIconSection, 'text/html');
        // Read only Shopify's visible numeric span. Reading the entire bubble
        // also includes its visually-hidden accessibility copy (4 + 4 = 44).
        const countNode = iconDocument.querySelector('.cart-count-bubble [aria-hidden="true"]');
        itemCount = Number.parseInt(countNode?.textContent, 10);
      }
    }

    const renderCount = (count) => {
      const normalizedCount = Math.max(0, Number.parseInt(count, 10) || 0);
      document.querySelectorAll('[data-evori-cart-trigger] .cart-badge').forEach((badge) => {
        badge.textContent = String(normalizedCount);
      });
    };

    if (Number.isFinite(itemCount)) {
      renderCount(itemCount);
      return;
    }

    fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => response.json())
      .then((cart) => renderCount(cart.item_count))
      .catch((error) => console.error(error));
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
